import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";

export interface StepConfig {
  id: string;
  label: string;
  optional?: boolean;
}

export interface StepperContextType {
  activeStep: number;
  completedSteps: Set<number>;
  steps: StepConfig[];
  goToStep: (stepIndex: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  completeStep: (stepIndex: number) => void;
  incompleteStep: (stepIndex: number) => void;
  resetStepper: () => void;
  isStepCompleted: (stepIndex: number) => boolean;
  isStepActive: (stepIndex: number) => boolean;
  canGoToStep: (stepIndex: number) => boolean;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

interface StepperProviderProps {
  children: ReactNode;
  steps?: StepConfig[];
  initialStep?: number;
}

export const StepperProvider = ({
  children,
  steps = [],
  initialStep = 0,
}: StepperProviderProps) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setActiveStep(stepIndex);
      }
    },
    [steps.length],
  );

  const nextStep = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  }, [steps.length]);

  const previousStep = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const completeStep = useCallback((stepIndex: number) => {
    setCompletedSteps((prev) => new Set(prev).add(stepIndex));
  }, []);

  const incompleteStep = useCallback((stepIndex: number) => {
    setCompletedSteps((prev) => {
      const newSet = new Set(prev);
      newSet.delete(stepIndex);
      return newSet;
    });
  }, []);

  const resetStepper = useCallback(() => {
    setActiveStep(initialStep);
    setCompletedSteps(new Set());
  }, [initialStep]);

  const isStepCompleted = useCallback(
    (stepIndex: number) => completedSteps.has(stepIndex),
    [completedSteps],
  );

  const isStepActive = useCallback(
    (stepIndex: number) => activeStep === stepIndex,
    [activeStep],
  );

  const canGoToStep = useCallback(
    (stepIndex: number) => {
      // Can go to completed steps or the next step after the last completed
      if (completedSteps.has(stepIndex)) return true;
      if (stepIndex === 0) return true;

      // Check if all previous steps are completed
      for (let i = 0; i < stepIndex; i += 1) {
        if (!completedSteps.has(i) && !steps[i].optional) {
          return false;
        }
      }
      return true;
    },
    [completedSteps, steps],
  );

  const value = useMemo(
    () => ({
      activeStep,
      completedSteps,
      steps,
      goToStep,
      nextStep,
      previousStep,
      completeStep,
      incompleteStep,
      resetStepper,
      isStepCompleted,
      isStepActive,
      canGoToStep,
    }),
    [
      activeStep,
      completedSteps,
      steps,
      goToStep,
      nextStep,
      previousStep,
      completeStep,
      incompleteStep,
      resetStepper,
      isStepCompleted,
      isStepActive,
      canGoToStep,
    ],
  );

  return (
    <StepperContext.Provider value={value}>{children}</StepperContext.Provider>
  );
};

export const useStepper = (): StepperContextType => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error("useStepper must be used within a StepperProvider");
  }
  return context;
};

export default StepperContext;
